import React from "react";
import classNames from "classnames";

import { track } from "../../analytics";
import { ItemList } from "../ItemList";
import { IItem } from "../ItemList/IItem";
import { PickerSearchField } from "../PickerSearchField";
import * as styles from "./PickerTabGroup.scss";
import { Scrollable } from "./Scrollable";

interface IPickerTabGroup {
    items: IPickerItem[];
    selectedTab: string;
    onClick: (id: string) => void;
    placeholder?: string;
    sorter?: (a: IItem, b: IItem) => number;
}

export interface IPickerItem {
    id: string;
    name: string;
    heading?: string;
    items: IItem[];
}

interface IPickerTab {
    id: string;
    name: string;
    isActive: boolean;
    onClick: (id: string) => void;
}

const searchFunction = (search: string) => (item: IItem) =>
    item.label.toLowerCase().includes(search.toLowerCase());

const sortLabels = (a: IItem, b: IItem) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const PickerTabGroup = ({
    items: tabs,
    selectedTab,
    onClick,
    placeholder,
    sorter = sortLabels,
}: IPickerTabGroup) => {
    const [search, setSearch] = React.useState("");
    const isSearching = search.length > 0;
    return (
        <>
            <PickerSearchField
                value={search}
                onChange={s => setSearch(s)}
                className={styles.search}
                placeholder={placeholder || "Search Modules..."}
            />
            {!isSearching && (
                <ul className={styles.tabGroup}>
                    {tabs.map(({ id, name }) => (
                        <Tab
                            key={id}
                            id={id}
                            name={name}
                            isActive={id === selectedTab}
                            onClick={onClick}
                        />
                    ))}
                </ul>
            )}
            <Scrollable className={classNames(styles.scrollable)}>
                <div className={styles.tabContent}>
                    {tabs
                        .filter(({ id }) => id === selectedTab || isSearching)
                        .map(({ id, name, items, heading }) => {
                            const viewList = items
                                .filter(searchFunction(search))
                                .sort(sorter);
                            return (
                                <div key={`tab-${id}`}>
                                    {viewList.length > 0 && (
                                        <div className={styles.name}>
                                            {heading || name}
                                        </div>
                                    )}
                                    <ItemList items={viewList} />
                                </div>
                            );
                        })}
                </div>
            </Scrollable>
        </>
    );
};

const Tab = ({ id, name, isActive, onClick }: IPickerTab) => {
    const handleClick = () => {
        track("Picker Tab", { name });
        onClick(id);
    };

    return (
        <li className={styles.tab} data-testid={`picker-tab-${name}`}>
            <span
                onClick={handleClick}
                className={classNames(
                    styles.label,
                    isActive && styles.selected,
                )}
            >
                {name}
            </span>
        </li>
    );
};

export default PickerTabGroup;
